import type { CogniteClient, QueryRequest } from '@cognite/sdk';
import { z } from 'zod';

import { extractTsRefExternalId, tsRefCell } from '#core/lib/cognite/tsRef.ts';
import { type DataModelView, KG_QUERY_MAX_LIMIT, viewToSchema } from '#core/lib/cognite/view.ts';
import { NO_POS } from '#core/lib/expr/ast.ts';
import type { SourceFetcher } from '#core/lib/expr/EvalContext.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import { translatePredicateToCdf } from '#core/lib/expr/ops/knowledge-graph/pushdown.ts';
import { parseQkgSort } from '#core/lib/expr/ops/knowledge-graph/sort.ts';
import type { QkgSource } from '#core/lib/expr/plan.ts';
import { type ColumnType, type Schema, unwrapNullable } from '#core/lib/expr/schema/types.ts';
import { type Cell, type Columns, MaterializedDataset } from '#core/lib/expr/value.ts';

import { outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetSummary } from '#plugin-chat/lib/tool/dataset-summary.ts';

/**
 * LLM-facing input for {@link QueryKnowledgeGraphTool}. The LLM writes a
 * single s-expression (typically wrapping
 * `(query-knowledge-graph "View" :where ... :limit ...)`) that evaluates
 * to a `Dataset`, plus an `outputDataset` key. The query is executed and
 * the materialised result is staged under `outputDataset`; downstream
 * tools reference it by that name (e.g. `(transform (col <outputDataset>)
 * ...)`) — they do not re-pass the s-expression.
 */
export const QueryKnowledgeGraphInputSchema = z.object({
  expression: z
    .string()
    .describe(
      'An s-expression that evaluates to a Dataset. Typically built around `(query-knowledge-graph "View" :where ... :sort [[... asc|desc] ...] :limit N)`.',
    ),
  outputDataset: outputDatasetSchema,
});

/** @public */
export type QueryKnowledgeGraphInput = z.infer<typeof QueryKnowledgeGraphInputSchema>;

/**
 * Result envelope returned from the tool's `execute` to the LLM. The
 * expression is evaluated **and materialised** (CDF round-trip) at
 * execute time, so the LLM receives a real column profile + row sample
 * — not just a schema — and any CDF failure surfaces through the
 * structured error envelope instead of only in the UI. The materialised
 * result is cached on `session.fetch`, so the later `render`
 * (`useMaterializedExpr`) and any chained tool reuse it without a second
 * round-trip.
 *
 * @public
 */
export type QueryKnowledgeGraphOutput = Readonly<{
  /** Success discriminator — every tool success envelope carries this
   * (matches `ToolOutputSuccessSchema`); failures carry `success: false`
   * via the structured error envelope instead. */
  success: true;
  /** `MaterializedDataset.state` tag — `'materialized'` after the eager
   * materialisation; kept as a string so a future direct-materialized
   * path round-trips verbatim. */
  state: string;
  /** Source descriptor's view/`kind` (`qkg` view externalId, or
   * `'materialized'` when the expression is already materialised). */
  source: string;
  /** Key the materialised result was staged under — downstream tools
   * reference this name instead of re-passing the expression. */
  outputDataset: string;
  /** Generic dataset summary (column profile + capped sample +
   * data-quality notes) rendered for the LLM via `<DatasetPreview>`. */
  summary: DatasetSummary;
}>;

/**
 * CDF instance-query node response shape, narrowed to the fields the
 * fetcher reads. CDF nests declared view properties under
 * `properties[viewSpace][viewExternalId/viewVersion][propName]`; the
 * node-metadata fields (`space`, `externalId`, `createdTime`,
 * `lastUpdatedTime`) live at the top level.
 */
type CdfNodeProperties = Readonly<Record<string, Readonly<Record<string, unknown>>>>;
export type CdfNodeRow = Readonly<{
  space?: string;
  externalId?: string;
  createdTime?: unknown;
  lastUpdatedTime?: unknown;
  properties?: Readonly<Record<string, CdfNodeProperties>>;
}>;

/**
 * Translate the verbatim `:sort` AST captured on a `QkgSource` into the CDF
 * instance-query sort spec. Shape validation is delegated to the SSOT
 * `parseQkgSort` (core) — which `qkg`'s `apply` already ran eagerly with a
 * real `SourcePos`; this call is the defence-in-depth re-parse for direct
 * JS-object `AstInput` that bypasses the op. A malformed sort therefore
 * surfaces as a retryable `ShapeError`, never a raw `TypeError`.
 */
export const translateSortToCdf = (
  sort: AstInput,
  view: DataModelView,
): readonly { property: unknown; direction: 'ascending' | 'descending' }[] => {
  const property = view.property as Record<string, unknown>;
  return parseQkgSort(sort, NO_POS).map(({ column, descending }) => ({
    property: property[column],
    direction: descending ? ('descending' as const) : ('ascending' as const),
  }));
};

/**
 * Build the CDF instance-query payload for a {@link QkgSource}. Folds the
 * pushable filter (`source.where`, translated via
 * `translatePredicateToCdf`) into a top-level `and` alongside a `hasData`
 * clause so CDF returns only nodes that actually carry the view's data.
 */
export const buildCdfQuery = (source: QkgSource, view: DataModelView): QueryRequest => {
  const filterClauses: readonly unknown[] = [
    ...(source.where === undefined ? [] : [translatePredicateToCdf(source.where, view)]),
    { hasData: [view] },
  ];
  const sortSpec = source.sort === undefined ? undefined : translateSortToCdf(source.sort, view);
  const query = {
    with: {
      [source.view]: {
        nodes: { filter: { and: filterClauses } },
        limit: source.limit ?? KG_QUERY_MAX_LIMIT,
        ...(sortSpec === undefined ? {} : { sort: sortSpec }),
      },
    },
    select: {
      [source.view]: {
        sources: [{ source: view, properties: ['*'] }],
      },
    },
  };
  return query as unknown as QueryRequest;
};

/**
 * Project a single CDF node row's value at `colName` to a Cell. CDF nests
 * declared view properties under `properties[viewSpace][viewKey][prop]`;
 * node-metadata fields (`createdTime`, `lastUpdatedTime`) and the scalar
 * identity columns (`space`, `externalId`) live at the top level.
 *
 * Type-driven branches:
 *   - `nested-dataset` columns (today: CogniteTimeSeries properties via
 *     `viewToSchema`) project to a `LazyDataset` over a `ts-ref` source
 *     so downstream consumers can `(unnest …)` to flatten the inner
 *     `{timestamp, value, isString, unit}` rows on demand without
 *     forcing the datapoints fetch up-front. The CDF reference shape
 *     is `{space, externalId}`; we capture only the `externalId` because
 *     the ts-ref fetcher disambiguates by id alone.
 */
export const projectCell = (
  row: CdfNodeRow,
  colName: string,
  colType: ColumnType,
  viewSpace: string,
  viewKey: string,
): Cell => {
  if (colName === 'space') {
    return (row.space ?? '') as Cell;
  }
  if (colName === 'externalId') {
    return (row.externalId ?? '') as Cell;
  }
  if (colName === 'createdTime') {
    return row.createdTime as Cell;
  }
  if (colName === 'lastUpdatedTime') {
    return row.lastUpdatedTime as Cell;
  }
  const propValue = row.properties?.[viewSpace]?.[viewKey]?.[colName];
  if (colType.kind === 'nested-dataset') {
    // SSOT: accept BOTH CDF encodings (bare externalId string OR
    // {space, externalId} object) via extractTsRefExternalId, and build
    // the cell only through tsRefCell — so a column the schema typed
    // nested-dataset can never project to a non-Dataset cell.
    const externalId = extractTsRefExternalId(propValue);
    return externalId === null ? null : tsRefCell(externalId, colType.innerSchema);
  }
  if (propValue === undefined || propValue === null) {
    return null;
  }
  // A CDF property can come back as "" for a column `viewToSchema`
  // declared numeric; left intact it violates the numeric column type
  // (degrading the column to `unknown` and emptying downstream
  // aggregates/sorts) — the same hazard the ts-ref `seriesToColumns`
  // projection guards against. String columns keep "" verbatim.
  if (propValue === '' && unwrapNullable(colType).kind === 'number') {
    return null;
  }
  return propValue as Cell;
};

/**
 * Project CDF instance-query node rows into a column-major `Columns` shape
 * matching the engine `Schema` for the view. Iterates the schema's column
 * order so every column reaches the same row count even when CDF omits
 * unset optional properties from the response.
 */
export const rowsToColumns = (rows: readonly CdfNodeRow[], schema: Schema, view: DataModelView): Columns => {
  const viewSpace = view.space;
  const viewKey = `${view.externalId}/${view.version}`;
  // Capture `(name, colType)` once per schema column — projectCell's
  // type-driven branches (nested-dataset → LazyDataset) read colType
  // and the cost is per-row otherwise.
  const colEntries = Array.from(schema.entries());
  const columns: Record<string, Cell[]> = Object.fromEntries(colEntries.map(([name]) => [name, [] as Cell[]]));
  for (const row of rows) {
    for (const [name, colType] of colEntries) {
      columns[name]?.push(projectCell(row, name, colType, viewSpace, viewKey));
    }
  }
  return columns;
};

/**
 * `SourceFetcher<QkgSource>` factory. Closes over a CogniteClient + the
 * available views; `<ChatSessionProvider>` memoises one instance per
 * `(client, views)` and mounts it on the engine's `sourceFetchers`. The
 * fetcher itself receives only `SourceFetcherCtx` (session + signal) —
 * the engine intentionally narrows the surface so fetchers cannot
 * recurse back into expression evaluation.
 */
export const buildQkgFetcher = (client: CogniteClient, views: readonly DataModelView[]): SourceFetcher<QkgSource> => {
  return async (source) => {
    const view = views.find((v) => v.externalId === source.view);
    if (view === undefined) {
      // qkg.apply verified the view existed before packaging the source;
      // this branch is a defensive guard for caller-side wiring bugs.
      throw new TypeError(`QKG fetcher: unknown view ${source.view}`);
    }
    const cdfQuery = buildCdfQuery(source, view);
    const response = await client.instances.query(cdfQuery);
    const items = (response.items?.[source.view] ?? []) as readonly CdfNodeRow[];
    const schema = viewToSchema(view);
    const columns = rowsToColumns(items, schema, view);
    return new MaterializedDataset(columns, schema);
  };
};
